import { createContext, useContext, useState } from 'react';

const LangContext = createContext();

const translations = {
  en: {
    badge: 'Mestre, Venice · Since 2023',
    heroTitle1: 'Where Tradition',
    heroTitle2: 'Meets',
    heroTitle3: 'Elegance',
    heroSub: 'Sarees · Lehengas · Designer Suits · Kurtas',
    heroDesc: 'Bringing the finest traditional and designer wear from India and Pakistan to the heart of Venice. From Banarasi sarees to Shivali gowns, discover exquisite craftsmanship and timeless silhouettes.',
    explore: 'Explore Collections',
    bookVisit: 'Book a Visit',
    navCollections: 'Collections',
    navLookbook: 'Lookbook',
    navHeritage: 'Heritage',
    navVisit: 'Visit Us',
    messageUs: 'Message Us',
    collectionsTag: 'Our Specialities',
    collectionsTitle: 'Curated',
    collectionsTitleEm: 'Collections',
    collectionsDesc: 'Each piece tells a story — sourced directly from artisans in India and Pakistan, celebrating centuries of tradition.',
    viewOnFb: 'View on Facebook',
    disclaimer: 'Images shown are examples of the styles we carry.',
    disclaimerLink: 'Visit our Facebook page',
    disclaimerEnd: 'to see our real, authentic collection.',
    galleryTag: 'Lookbook',
    galleryTitle: 'Latest',
    galleryTitleEm: 'Arrivals',
    galleryDesc: 'Fresh stock every season — discover our newest S4U, Shivali, Sayuri and Banarasi pieces.',
    galleryDisclaimer: 'Example images shown.',
    galleryDisclaimerLink: 'See our real collection on Facebook',
    heritageQuote: '"We bring the colours of India & Pakistan directly to you — in the heart of Venice."',
    heritageDesc: 'At Isha Boutique, every saree has a story, every embroidery has a region, every thread connects you to generations of skilled artisans. We curate designer brands like Shivali, S4U, Sayuri, and Aashirwad — so you wear only the best.',
    findUs: 'Find Us',
    visitTitle: 'Visit Our',
    visitTitleEm: 'Boutique',
    visitDesc: 'Come see our full collection in person. We\'re conveniently located in Mestre — just a short journey from central Venice.',
    address: 'Address',
    phone: 'Phone',
    social: 'Social',
    hours: 'Hours',
    hoursValue: 'Mon–Sat: 09:30 – 21:00 (Closed 14:00 – 15:30 sporadically). For exact hours, contact us on Facebook or WhatsApp',
    footerDesc: 'Curating the finest sarees, lehengas, and salwar kameez from India and Pakistan — now in the heart of Mestre, Venice.',
    footerCollections: 'Collections',
    footerContact: 'Get In Touch',
    dividerText: 'Traditional · Elegant · Handcrafted',
    designs: 'Designs',
    origin: 'Origin',
    location: 'Location',
  },
  it: {
    badge: 'Mestre, Venezia · Dal 2023',
    heroTitle1: 'Dove la Tradizione',
    heroTitle2: 'Incontra',
    heroTitle3: "l'Eleganza",
    heroSub: 'Saree · Lehenga · Abiti Designer · Kurta',
    heroDesc: "Portiamo i migliori capi tradizionali e di design dall'India e dal Pakistan nel cuore di Venezia. Dai saree Banarasi agli abiti Shivali, scopri un artigianato squisito e silhouette senza tempo.",
    explore: 'Esplora le Collezioni',
    bookVisit: 'Prenota una Visita',
    navCollections: 'Collezioni',
    navLookbook: 'Lookbook',
    navHeritage: 'Heritage',
    navVisit: 'Visitaci',
    messageUs: 'Scrivici',
    collectionsTag: 'Le Nostre Specialità',
    collectionsTitle: 'Collezioni',
    collectionsTitleEm: 'Curate',
    collectionsDesc: "Ogni pezzo racconta una storia — acquistato direttamente dagli artigiani in India e Pakistan, celebrando secoli di tradizione.",
    viewOnFb: 'Vedi su Facebook',
    disclaimer: 'Le immagini mostrate sono esempi degli stili che proponiamo.',
    disclaimerLink: 'Visita la nostra pagina Facebook',
    disclaimerEnd: 'per vedere la nostra vera collezione.',
    galleryTag: 'Lookbook',
    galleryTitle: 'Ultimi',
    galleryTitleEm: 'Arrivi',
    galleryDesc: 'Nuovi arrivi ogni stagione — scopri i nostri nuovi pezzi S4U, Shivali, Sayuri e Banarasi.',
    galleryDisclaimer: 'Immagini di esempio.',
    galleryDisclaimerLink: 'Vedi la vera collezione su Facebook',
    heritageQuote: '"Portiamo i colori di India e Pakistan direttamente da te — nel cuore di Venezia."',
    heritageDesc: "Da Isha Boutique, ogni saree ha una storia, ogni ricamo ha una regione, ogni filo ti collega a generazioni di artigiani esperti. Curiamo marchi designer come Shivali, S4U, Sayuri e Aashirwad — così indossi solo il meglio.",
    findUs: 'Trovaci',
    visitTitle: 'Visita la Nostra',
    visitTitleEm: 'Boutique',
    visitDesc: 'Vieni a vedere la nostra collezione completa di persona. Siamo comodamente situati a Mestre — a un breve viaggio dal centro di Venezia.',
    address: 'Indirizzo',
    phone: 'Telefono',
    social: 'Social',
    hours: 'Orario',
    hoursValue: 'Lun–Sab: 09:30 – 21:00 (Chiuso 14:00 – 15:30 saltuariamente). Per orari precisi, contattaci su Facebook o WhatsApp',
    footerDesc: "Curiamo i migliori saree, lehenga e salwar kameez dall'India e dal Pakistan — ora nel cuore di Mestre, Venezia.",
    footerCollections: 'Collezioni',
    footerContact: 'Contattaci',
    dividerText: 'Tradizionale · Elegante · Artigianale',
    designs: 'Modelli',
    origin: 'Origine',
    location: 'Posizione',
  },
};

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('isha-lang') || 'en');

  const toggle = () => {
    const next = lang === 'en' ? 'it' : 'en';
    setLang(next);
    localStorage.setItem('isha-lang', next);
  };

  const t = translations[lang];

  return (
    <LangContext.Provider value={{ lang, toggle, t }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
